import { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorApi } from '../../api/problema';
import type { Adjunto, Conversacion, Mensaje } from '../../api/ops/buzon';
import { claveIdempotencia } from '../../api/comun';
import { usePermisos } from '../../permisos/permisos';
import type { Operaciones } from '../../api/operaciones';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Deshacer } from '../../componentes/Deshacer';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { Ventana } from '../../componentes/Ventana';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { Menu, type OpcionMenu } from '../../componentes/Menu';
import { textoDeError } from './errores';
import { FichaDelContacto, VentanaVincular } from './Ficha';
import { EscribirConPlantilla } from './Plantilla';
import {
    accionesDe,
    acuseDe,
    ADJUNTOS_POR_CANAL,
    errorDeAdjunto,
    franjaDeSaldo,
    RUTA_MENSAJES_PLAN,
    avisoDelMensaje,
    borrado,
    canal,
    claveDeDia,
    esLineaDeSistema,
    respuestaAutomatica,
    etiquetaAdjunto,
    horaDeMensaje,
    iniciales,
    nombreDe,
    pieDe,
    primerNombre,
    separadorDeDia,
    tamano,
    textoEstado,
    type Quien,
} from './reglas';

interface Props {
    id: string;
    /** Lo que ya trae la lista (se pinta mientras llega la conversación). */
    resumen: Conversacion | null;
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    soloLectura: boolean;
    quien: Quien;
    nombres: ReadonlyMap<string, string>;
    ahora: Date;
    zona?: string;
    navegar: (ruta: string) => void;
    onVolver: () => void;
    onCambio: (c: Conversacion) => void;
}

/**
 * Una conversación del Buzón (11.3): cabecera en una línea, historial completo (del más antiguo al más reciente, «Ver
 * mensajes anteriores» con el cursor) y el pie (tomar, escribir o plantilla). Asignar, fijar, cerrar y vincular van con
 * «Deshacer» 5 s (G159); tomar, responder y devolver salen en el momento. Los adjuntos se suben al elegirlos
 * (`uploadConversationAttachment`, G727) y salen con el texto (`media_upload_ids`); «Reintentar» solo si el mensaje es
 * `retryable` (en todos los canales, también el email, G750). El nivel del saldo de WhatsApp lo da la conversación
 * (`whatsapp_balance_level`, G726).
 */
export function Hilo({ id, resumen, ops, negocio, movil, soloLectura, quien, nombres, ahora, zona, navegar, onVolver, onCambio }: Props) {
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const { puede } = usePermisos();
    const [adjuntos, setAdjuntos] = useState<Adjunto[]>([]);
    const [subiendo, setSubiendo] = useState(false);
    const [reintentando, setReintentando] = useState<string | null>(null);
    const selector = useRef<HTMLInputElement>(null);
    const [conv, setConv] = useState<{ datos: Conversacion; version: string } | null>(null);
    const [mensajes, setMensajes] = useState<Mensaje[] | null>(null);
    const [anteriores, setAnteriores] = useState<string | null>(null);
    const [cargandoAnteriores, setCargandoAnteriores] = useState(false);
    const [error, setError] = useState(false);
    const [borrador, setBorrador] = useState('');
    const [enviando, setEnviando] = useState(false);
    // G713: sin mensajes de WhatsApp, el envío queda desactivado hasta comprar o renovar.
    const [sinSaldo, setSinSaldo] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [panel, setPanel] = useState<'plantilla' | 'cliente' | null>(null);
    const [ventana, setVentana] = useState<'devolver' | 'vincular' | 'asignar' | null>(null);
    const [resumenDevolver, setResumenDevolver] = useState('');
    const caja = useRef<HTMLDivElement>(null);
    const [alFinal, setAlFinal] = useState(0);

    const cargar = useCallback(
        (marcarLeida: boolean) => Promise.all([ops.getConversation(negocio, id), ops.listConversationMessages(negocio, id, { limit: 50 })])
                .then(([c, m]) => {
                    const datos = c.datos;
                    setConv({ datos, version: c.version ?? '' });
                    setMensajes([...m.data].reverse());
                    setAnteriores(m.next_cursor);
                    setAlFinal((n) => n + 1);
                    if (marcarLeida && datos.unread_count > 0) {
                        ops.markConversationRead(negocio, id)
                            .then(() => { onCambio({ ...datos, unread_count: 0 }); })
                            .catch(() => undefined);
                    } else {
                        onCambio(datos);
                    }
                })
                .catch(() => { setError(true); }),
        [ops, negocio, id, onCambio],
    );

    useEffect(() => {
        void cargar(true);
    }, [cargar]);

    useEffect(() => {
        const el = caja.current;
        if (el !== null) {
            el.scrollTop = el.scrollHeight;
        }
    }, [alFinal]);

    const verAnteriores = () => {
        if (anteriores === null) {
            return;
        }
        setCargandoAnteriores(true);
        ops.listConversationMessages(negocio, id, { cursor: anteriores, limit: 50 })
            .then((m) => {
                setMensajes((x) => [...[...m.data].reverse(), ...(x ?? [])]);
                setAnteriores(m.next_cursor);
            })
            .catch(() => { avisar({ tipo: 'error', texto: 'No he podido cargar los mensajes anteriores. Prueba de nuevo.' }); })
            .finally(() => { setCargandoAnteriores(false); });
    };

    const c = conv?.datos ?? resumen;
    if (c === null) {
        return <section className="rv-bz-hilo rv-bz-hilo--nada" aria-busy="true"><span>Cargando conversaciones…</span></section>;
    }
    const nombre = nombreDe(c);
    const [nombreCanal, iconoCanal] = canal(c.channel_type);
    const estado = textoEstado(c, quien, nombres);
    const acc = accionesDe(c, quien, soloLectura);
    const pie = pieDe(c, quien, soloLectura, nombres, ahora, zona);
    const verPlan = puede('plan.ver');
    // G713, G726: sin mensajes de WhatsApp (el 409, el motivo del paso al buzón o el nivel de la conversación) no se envía.
    const agotado = c.channel_type === 'whatsapp' && (sinSaldo || c.escalation_reason === 'whatsapp_no_balance' || c.whatsapp_balance_level === 'empty');
    const pocos = c.whatsapp_balance_level === 'low' ? franjaDeSaldo('low', verPlan) : null;
    // G727: adjuntos según el canal; fuera de la ventana el pie ya es de plantilla.
    const tiposAdjunto = ADJUNTOS_POR_CANAL[c.channel_type] ?? [];

    /** Acción que sale en el momento y devuelve la conversación. */
    const accion = (hacer: () => Promise<Conversacion>, porDefecto: string, despues?: () => void) => {
        setOcupado(true);
        hacer()
            .then(() => {
                despues?.();
                return cargar(false);
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: textoDeError(e, porDefecto) }); })
            .finally(() => { setOcupado(false); });
    };

    /** Acción con «Deshacer» 5 s: se ve al momento y la petición sale a los 5 s salvo «Deshacer». */
    const conDeshacer = (texto: string, local: Partial<Conversacion>, hacer: () => Promise<unknown>) => {
        const antes = conv;
        if (antes !== null) {
            const nueva = { ...antes.datos, ...local };
            setConv({ ...antes, datos: nueva });
            onCambio(nueva);
        }
        envio.programar({
            texto,
            donde: 'hilo',
            enviar: () => {
                hacer()
                    .then(() => cargar(false))
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido hacerlo. Prueba de nuevo.') });
                        void cargar(false);
                    });
            },
            alDeshacer: () => {
                if (antes !== null) {
                    setConv(antes);
                    onCambio(antes.datos);
                }
            },
        });
    };

    const tomar = () => { accion(() => ops.takeConversation(negocio, id), 'No he podido tomar la conversación. Prueba de nuevo.'); };
    const cerrar = () => { conDeshacer('Conversación cerrada.', { state: 'closed', unread_count: 0 }, () => ops.closeConversation(negocio, id)); };
    const fijar = () => {
        const fijada = c.human_mode_pinned !== true;
        conDeshacer(fijada ? 'Fijada con una persona.' : 'Ya no está fijada.', { human_mode_pinned: fijada }, () => ops.updateConversation(negocio, id, { human_mode_pinned: fijada }, conv?.version ?? ''));
    };
    const asignar = (miembro: string | null) => {
        setVentana(null);
        if (miembro === (c.assigned_member_id ?? null)) {
            return;
        }
        const texto = miembro === null ? 'Ahora no la tiene asignada nadie.' : `Asignada a ${nombres.get(miembro) ?? 'otra persona del equipo'}.`;
        conDeshacer(texto, { assigned_member_id: miembro }, () => ops.updateConversation(negocio, id, { assigned_member_id: miembro }, conv?.version ?? ''));
    };
    const vincular = (cliente: string | null, nombreCliente: string) => {
        setVentana(null);
        setPanel(null);
        conDeshacer(cliente === null ? `Desvinculada de ${nombreCliente}.` : `Vinculada a ${nombreCliente}.`, { contact: { ...c.contact, customer_id: cliente } }, () => ops.linkConversationCustomer(negocio, id, cliente));
    };
    const devolver = () => {
        const texto = resumenDevolver.trim();
        accion(() => ops.returnConversationToAssistant(negocio, id, texto), 'No he podido devolverla al asistente. Prueba de nuevo.', () => {
            setVentana(null);
            setResumenDevolver('');
        });
    };
    const elegirArchivo = (archivo: File | undefined) => {
        if (archivo === undefined) {
            return;
        }
        const mal = errorDeAdjunto(archivo, c.channel_type);
        if (mal !== null) {
            avisar({ tipo: 'error', texto: mal });
            return;
        }
        setSubiendo(true);
        ops.uploadConversationAttachment(negocio, id, archivo, claveIdempotencia())
            .then((a) => { setAdjuntos((x) => [...x, a]); })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido subir el archivo. Prueba de nuevo.') }); })
            .finally(() => { setSubiendo(false); });
    };
    const reintentar = (m: Mensaje) => {
        setReintentando(m.id);
        ops.retryConversationMessage(negocio, id, m.id)
            .then((nuevo) => { setMensajes((x) => (x ?? []).map((v) => (v.id === m.id ? nuevo : v))); })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido reintentarlo. Prueba de nuevo.') });
                void cargar(false);
            })
            .finally(() => { setReintentando(null); });
    };
    const enviar = () => {
        const texto = borrador.trim();
        if (texto === '' || enviando || subiendo) {
            return;
        }
        setEnviando(true);
        ops.replyToConversation(negocio, id, adjuntos.length === 0 ? { text: texto } : { text: texto, media_upload_ids: adjuntos.map((a) => a.id) })
            .then((m) => {
                setBorrador('');
                setAdjuntos([]);
                setAlFinal((n) => n + 1);
                setMensajes((x) => [...(x ?? []), m]);
                void cargar(false);
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 409 && (e.problema as Record<string, unknown>).channel_reason === 'no_balance') {
                    setSinSaldo(true);
                }
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido enviar el mensaje. Prueba de nuevo.') });
            })
            .finally(() => { setEnviando(false); });
    };

    const estrecha = movil;
    const opciones: OpcionMenu[] = [];
    if (estrecha && acc.devolver) {
        opciones.push({ clave: 'devolver', texto: 'Devolver al asistente', icono: 'sparkles', onClick: () => { setVentana('devolver'); } });
    }
    if (acc.asignar) {
        opciones.push({ clave: 'asignar', texto: 'Asignar a…', icono: 'headset', onClick: () => { setVentana('asignar'); } });
    }
    if (acc.fijar) {
        opciones.push({ clave: 'fijar', texto: c.human_mode_pinned === true ? 'Quitar el fijado' : 'Fijar con una persona', icono: 'pin', onClick: () => { fijar(); } });
    }
    opciones.push({ clave: 'ficha', texto: 'Ver la ficha del cliente', icono: 'user', onClick: () => { setPanel('cliente'); } });
    if (acc.cerrar) {
        opciones.push({ clave: 'cerrar', texto: 'Cerrar la conversación', icono: 'archive', peligro: true, onClick: () => { cerrar(); } });
    }

    const asignables: { valor: string | null; texto: string }[] = quien.todas
        ? [{ valor: null, texto: 'Sin asignar' }, ...[...nombres.entries()].map(([m, n]) => ({ valor: m, texto: m === quien.yo ? `${n} (tú)` : n }))]
        : [...(quien.yo === null ? [] : [{ valor: quien.yo, texto: `${nombres.get(quien.yo) ?? 'Tú'} (tú)` }]), { valor: null, texto: 'Sin asignar' }];

    return (
        <section className={`rv-bz-hilo${movil ? ' rv-bz-hilo--movil' : ''}`} role={movil ? 'dialog' : 'region'} aria-label={`Conversación con ${nombre}`}>
            <header className="rv-bz-hilo__cab">
                {movil && <button type="button" className="rv-bi rv-bz-volver" aria-label="Volver al Buzón" onClick={onVolver}><Icono nombre="chevron-left" /></button>}
                <button type="button" className="rv-bz-quien" aria-label={`Ver la ficha de ${nombre}`} onClick={() => { setPanel('cliente'); }}>
                    <span className={`rv-bz-av${(c.contact.customer_id ?? null) !== null ? ' rv-bz-av--rojo' : ''}`} aria-hidden="true">{iniciales(nombre)}</span>
                    <span className="rv-bz-hilo__quien">
                        <strong>{nombre}</strong>
                        <small className={estado.aviso ? 'rv-bz-aviso' : undefined}><Icono nombre={iconoCanal} />{estado.texto}</small>
                    </span>
                </button>
                {!estrecha && acc.devolver && <Boton tipo="secundario" icono="sparkles" onClick={() => { setVentana('devolver'); }}>Devolver al asistente</Boton>}
                {!movil && <button type="button" className="rv-bi" aria-label={`Ver la ficha de ${nombre}`} aria-pressed={panel === 'cliente'} title="Ficha del cliente" onClick={() => { setPanel('cliente'); }}><Icono nombre="user" /></button>}
                <Menu etiqueta="Más opciones de la conversación" movil={movil} opciones={opciones} />
            </header>
            <div className="rv-bz-hilo__msgs" ref={caja} role="log" aria-label="Mensajes">
                {error && (
                    <div className="rv-bz-vacio">
                        <strong>No he podido cargar las conversaciones</strong>
                        <Boton tipo="secundario" icono="refresh-cw" onClick={() => { setError(false); void cargar(false); }}>Reintentar</Boton>
                    </div>
                )}
                {anteriores !== null && (
                    <div className="rv-bz-mas">
                        <Boton tipo="secundario" icono="history" ocupado={cargandoAnteriores} ocupadoTexto="Cargando…" onClick={verAnteriores}>Ver mensajes anteriores</Boton>
                    </div>
                )}
                {mensajes === null && !error && <p className="rv-bz-sys"><span>Cargando conversaciones…</span></p>}
                {(mensajes ?? []).map((m, i, todos) => {
                    const previo = todos[i - 1];
                    const dia = previo === undefined || claveDeDia(previo.created_at, zona) !== claveDeDia(m.created_at, zona) ? separadorDeDia(m.created_at, ahora, zona) : null;
                    const grupo = dia === null && previo !== undefined && !esLineaDeSistema(previo) && previo.origin === m.origin && (previo.member_id ?? null) === (m.member_id ?? null);
                    return (
                        <div key={m.id} className="rv-bz-item">
                            {dia !== null && <span className="rv-bz-dia">{dia}</span>}
                            <Burbuja m={m} grupo={grupo} tipoCanal={c.channel_type} yo={quien.yo} nombres={nombres} zona={zona} onReintentar={!soloLectura && m.retryable === true && reintentando !== m.id ? () => { reintentar(m); } : null} />
                        </div>
                    );
                })}
            </div>
            <div className="rv-bz-pie">
                {envio.pendiente?.donde === 'hilo' && <Deshacer texto={envio.pendiente.texto} onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} fondo="suave" movil={movil} />}
                {pie.tipo === 'caja' && (
                    <div className={`rv-bz-caja rv-bz-caja--${pie.tono}`}>
                        <Icono nombre={pie.icono} />
                        <span><strong>{pie.titulo}</strong>{pie.texto === '' ? '' : ` ${pie.texto}`}</span>
                        {pie.boton !== null && (
                            <Boton tipo={pie.boton === 'tomar' ? 'principal' : 'secundario'} icono="headset" movil={movil} ocupado={ocupado} onClick={tomar}>Tomar la conversación</Boton>
                        )}
                    </div>
                )}
                {pie.tipo === 'plantilla' && (
                    <>
                        {pie.nota !== '' && <p className="rv-bz-ventana" role="note"><Icono nombre="clock" /><span>{pie.nota}</span></p>}
                        <div className="rv-bz-caja rv-bz-caja--gris">
                            <span>{pie.texto}</span>
                            <Boton icono="file-text" movil={movil} onClick={() => { setPanel('plantilla'); }}>Enviar plantilla</Boton>
                        </div>
                    </>
                )}
                {pie.tipo === 'escribir' && agotado && (
                    <p className="rv-bz-ventana rv-bz-ventana--roja" role="note">
                        <Icono nombre="wallet" />
                        <span>{verPlan ? 'Te has quedado sin mensajes de WhatsApp. Compra un paquete en Plan o espera a que se renueve.' : 'Sin mensajes de WhatsApp. Avisa al Propietario.'}</span>
                        {verPlan && <button type="button" className="rv-bz-enlace" onClick={() => { navegar(RUTA_MENSAJES_PLAN); }}>Añadir mensajes</button>}
                    </p>
                )}
                {pie.tipo === 'escribir' && !agotado && pocos !== null && (
                    <p className="rv-bz-ventana" role="note">
                        <Icono nombre="wallet" />
                        <span>{`${pocos.titulo}.`}</span>
                        {pocos.accion !== null && <button type="button" className="rv-bz-enlace" onClick={() => { navegar(RUTA_MENSAJES_PLAN); }}>{pocos.accion}</button>}
                    </p>
                )}
                {pie.tipo === 'escribir' && (
                    <>
                        {pie.avisoPrivacidad && <p className="rv-bz-ventana" role="note"><Icono nombre="shield-check" /><span>Tu primera respuesta llevará delante el aviso de privacidad.</span></p>}
                        {adjuntos.length > 0 && (
                            <div className="rv-bz-adjuntos">
                                {adjuntos.map((a) => (
                                    <span key={a.id} className="rv-bz-adjuntos__uno">
                                        <Icono nombre={a.kind === 'image' ? 'image' : a.kind === 'audio' ? 'audio-lines' : 'file-text'} />
                                        <span>{[etiquetaAdjunto(a.kind), tamano(a.size_bytes)].filter((t) => t !== '').join(' · ')}</span>
                                        <button type="button" className="rv-bi" aria-label={`Quitar ${etiquetaAdjunto(a.kind).toLowerCase()}`} onClick={() => { setAdjuntos((x) => x.filter((v) => v.id !== a.id)); }}><Icono nombre="x" /></button>
                                    </span>
                                ))}
                                {borrador.trim() === '' && <small>Escribe un texto para mandarlo con el adjunto.</small>}
                            </div>
                        )}
                        <div className="rv-bz-escribir">
                            {pie.plantillas && <button type="button" className="rv-bi" aria-label="Escribir con una plantilla" title="Plantillas" onClick={() => { setPanel('plantilla'); }}><Icono nombre="file-text" /></button>}
                            {tiposAdjunto.length > 0 && (
                                <>
                                    <button type="button" className="rv-bi" aria-label="Adjuntar una foto o un archivo" title="Adjuntar" disabled={subiendo} onClick={() => { selector.current?.click(); }}><Icono nombre={subiendo ? 'loader-circle' : 'paperclip'} /></button>
                                    <input ref={selector} type="file" hidden accept={tiposAdjunto.join(',')} data-testid="adjuntar" onChange={(e) => { elegirArchivo(e.target.files?.[0]); e.target.value = ''; }} />
                                </>
                            )}
                            <div className="rv-bz-escribir__campo">
                                <Campo etiqueta={`Escribe a ${primerNombre(nombre)}`} valor={borrador} onChange={setBorrador} filas={1} max={4096} nombre="respuesta" />
                            </div>
                            <Boton icono="send" movil={movil} ocupado={enviando} ocupadoTexto="Enviando…" disabled={borrador.trim() === '' || subiendo || (agotado && c.channel_type === 'whatsapp')} aria-label="Enviar" onClick={enviar}>Enviar</Boton>
                        </div>
                    </>
                )}
            </div>
            <Ventana
                abierta={ventana === 'devolver'}
                titulo="Devolver al asistente"
                texto={`${primerNombre(nombre)} volverá a hablar con el asistente. Si el asistente no puede seguir, la conversación vuelve al Buzón.`}
                accion="Devolver"
                accionIcono="sparkles"
                ocupado={ocupado}
                movil={movil}
                onAccion={devolver}
                onCerrar={() => { setVentana(null); }}
            >
                <Campo etiqueta="Resumen para el asistente (opcional)" valor={resumenDevolver} onChange={setResumenDevolver} filas={3} max={1000} ayuda="Lo que ya está resuelto, para que no lo repita." />
            </Ventana>
            <Ventana abierta={ventana === 'asignar'} titulo="Asignar a" movil={movil} onCerrar={() => { setVentana(null); }}>
                <div role="radiogroup" aria-label="Asignar a" className="rv-ca-bloque">
                    {asignables.map((o) => (
                        <OpcionRadio key={o.valor ?? 'nadie'} elegida={(c.assigned_member_id ?? null) === o.valor} onElegir={() => { asignar(o.valor); }} titulo={o.texto} icono={o.valor === null ? 'circle' : 'user'} />
                    ))}
                </div>
            </Ventana>
            {ventana === 'vincular' && <VentanaVincular ops={ops} negocio={negocio} movil={movil} onElegir={(cliente, n) => { vincular(cliente, n); }} onCerrar={() => { setVentana(null); }} />}
            <FichaDelContacto
                abierto={panel === 'cliente'}
                conversacion={c}
                ops={ops}
                negocio={negocio}
                movil={movil}
                puedeVincular={acc.vincular}
                zona={zona}
                onCerrar={() => { setPanel(null); }}
                onVincular={() => { setVentana('vincular'); }}
                onDesvincular={(n) => { vincular(null, n); }}
                onAbrir={(cliente) => { navegar(`/clientes?cliente=${encodeURIComponent(cliente)}`); }}
            />
            {panel === 'plantilla' && (
                <EscribirConPlantilla
                    conversacion={c}
                    nombreCanal={nombreCanal}
                    ops={ops}
                    negocio={negocio}
                    movil={movil}
                    onCerrar={() => { setPanel(null); }}
                    onEnviada={(m) => {
                        setPanel(null);
                        setAlFinal((n) => n + 1);
                        setMensajes((x) => [...(x ?? []), m]);
                        void cargar(false);
                    }}
                    navegar={navegar}
                />
            )}
        </section>
    );
}

function Burbuja({ m, grupo, tipoCanal, yo, nombres, zona, onReintentar }: { m: Mensaje; grupo: boolean; tipoCanal: string; yo: string | null; nombres: ReadonlyMap<string, string>; zona?: string; onReintentar: (() => void) | null }) {
    const hora = horaDeMensaje(m.created_at, zona);
    if (esLineaDeSistema(m)) {
        return <p className="rv-bz-sys"><Icono nombre="info" /><span>{`${m.body ?? ''} · ${hora}`}</span></p>;
    }
    const cliente = m.origin === 'customer';
    const equipo = m.origin === 'inbox';
    const mio = equipo && (m.member_id ?? null) !== null && m.member_id === yo;
    const fuera = borrado(m);
    const automatica = respuestaAutomatica(m);
    const quien: { texto: string; icono: NombreIcono | null } | null =
        automatica !== null ? automatica : m.origin === 'assistant' ? { texto: 'Asistente', icono: 'sparkles' } : m.origin === 'business_app' ? { texto: 'Desde la app WhatsApp Business', icono: null } : m.origin === 'entregas' ? { texto: 'Mensaje automático', icono: 'bell' } : equipo && !mio ? { texto: nombres.get(m.member_id ?? '') ?? 'Alguien del equipo', icono: null } : null;
    const acuse = acuseDe(m, tipoCanal);
    const aviso = avisoDelMensaje(m);
    const clase = fuera ? 'rv-bz-msg--borrado' : cliente ? 'rv-bz-msg--cliente' : equipo ? 'rv-bz-msg--equipo' : 'rv-bz-msg--auto';
    return (
        <div className={`rv-bz-msg ${clase}${grupo ? ' rv-bz-msg--grupo' : ''}${m.status === 'queued' ? ' rv-bz-msg--cola' : ''}`}>
            {quien !== null && !grupo && <span className="rv-bz-msg__quien">{quien.icono !== null && <Icono nombre={quien.icono} />}{quien.texto}</span>}
            <div className="rv-bz-burbuja">
                {aviso !== null && (
                    <span className="rv-bz-t40" title={`Versión ${m.privacy_notice_version ?? ''} del aviso`}>
                        <Icono nombre="shield-check" />
                        <span>{aviso}</span>
                    </span>
                )}
                {(m.media ?? []).map((x) =>
                    x.kind === 'image' ? (
                        <figure key={x.url} className="rv-bz-foto">
                            <img src={x.url} alt={etiquetaAdjunto(x.kind)} />
                            <figcaption>{[etiquetaAdjunto(x.kind), tamano(x.size_bytes)].filter((t) => t !== '').join(' · ')}</figcaption>
                        </figure>
                    ) : (
                        <a key={x.url} className="rv-bz-adjunto" href={x.url} target="_blank" rel="noreferrer">
                            <Icono nombre={x.kind === 'audio' ? 'audio-lines' : 'file-text'} />
                            <span>{etiquetaAdjunto(x.kind)}</span>
                            <small>{tamano(x.size_bytes)}</small>
                            <Icono nombre="download" />
                        </a>
                    ),
                )}
                {fuera ? <span className="rv-bz-burbuja__borrado">Mensaje borrado: pasó el plazo de conservación del historial.</span> : m.body != null && <span className={`rv-bz-burbuja__texto${m.redacted === true ? ' rv-bz-burbuja__texto--codigo' : ''}`}>{m.body}</span>}
                <span className="rv-bz-burbuja__meta">
                    <span>{m.status === 'queued' ? 'Enviando…' : hora}</span>
                    {acuse !== null && m.status !== 'queued' && <span aria-label={acuse[1]} title={acuse[1]}><Icono nombre={acuse[0]} /></span>}
                </span>
            </div>
            {m.attachment_replaced === true && <span className="rv-bz-sustituido"><Icono nombre="triangle-alert" />La imagen del QR no salió: se envió con el enlace a su pase.</span>}
            {m.status === 'failed' && (
                <span className="rv-bz-fallo">
                    <Icono nombre="circle-alert" />
                    No se ha enviado
                    {onReintentar !== null && (
                        <>
                            {' · '}
                            <button type="button" className="rv-bz-enlace" onClick={onReintentar}>Reintentar</button>
                        </>
                    )}
                </span>
            )}
        </div>
    );
}
